/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import { MerchantOnboardingRegistrationInfoRequest, OnboardingRequest } from "@/api/paymentApi";
import { msToyyyyMMdd } from "@/app/utils/common/functions/functions";
import countrySpecificBusinessStructure from "@/store/modules/onboarding/conditionalApiFields/countrySpecificBusinessStructure";
import { OnboardingSectionData } from "@/store/modules/onboarding/onboardingTypes";
import { copyFromApiResponse, copyFromErrorResponse } from "@/store/storeMappers";
import { validateAndGenerateError, validateAndSetValid } from "@/store/validationFromStore.js";
import { isSoleProprietor } from "@/data/type-of-business-list";

export enum BusinessRegistrationField {
}

export const businessRegistrationState: OnboardingSectionData = {
    api: "createMerchant",
    errors: {},
    dirty: false,
    data: {
        ...countrySpecificBusinessStructure.apiFields,
        incorporationDate: {
            apiPath: "business_info.incorporation_date",
            value: null,
            valid: false
        },
    },
};

const getters = {
    isValid: (state) => {
        return Object.keys(state.data).every(key => state.data[key].valid);
    },
    getErrors: (state) => {
        return state.errors
    },
    isDirty: (s) => {
        return s.dirty
    },
    totalInvalidFields: (s, getters, rootState, rootGetters) => {
        return Object.keys(s.data)
            .filter(key => {
                if (s.data[key].shouldShowInView) {
                    return s.data[key].shouldShowInView(rootGetters)
                } else {
                    return true;
                }
            })
            .filter(key => s.data[key].valid === false).length;
    },
    totalValidFields: (s, getters, rootState, rootGetters) => {
        return Object.keys(s.data)
            .filter(key => {
                if (s.data[key].shouldShowInView) {
                    return s.data[key].shouldShowInView(rootGetters)
                } else {
                    return true;
                }
            })
            .filter(key => s.data[key].valid === true).length
    },
    getField: (s) => {
        return (field: BusinessRegistrationField) => s.data[field].value 
    },
    getIncorporationDate: (s) => s.data.incorporationDate.value,
    ...countrySpecificBusinessStructure.getters,
    getCountrySpecificApiFields: () => countrySpecificBusinessStructure.apiFields,
    getTaxField: (s) => (apiPathSuffix) => {
        return Object.keys(s.data)
            .filter(key => s.data[key].apiPath === `business_info.tax_info.${apiPathSuffix}`)
            .map(key => s.data[key].value)
            .find(value => value !== null && value !== '');
    },
    getTaxId: (s, getters) => getters.getTaxField('tax_id'),
    getRegistrationNumber: (s, getters) => getters.getTaxField('registration_number'),
    getVatNumber: (s, getters) => getters.getTaxField('vat_number'),
    getIncrementalOnboardingData: (s, getters): MerchantOnboardingRegistrationInfoRequest => {
        return {
            requestType: OnboardingRequest.RequestTypeEnum.UPDATEREGISTRATIONINFO,
            incorporation_date: s.data.incorporationDate.value,
            tax_info: {
                tax_id: getters.getTaxId,
                registration_number: getters.getRegistrationNumber,
                vat_number: getters.getVatNumber,
            }
        }
    }
}

function mapApiPathToField(field: string, rootGetters: any): string {
    const isSole = isSoleProprietor(rootGetters['essentials/getTypeOfBusiness']);
    if (field === "tax_info.tax_id") {
        return isSole ? "soleProprietorTaxId" : "organisationTaxId";
    } else if (field === "tax_info.registration_number") {
        return isSole ? "soleProprietorRegistrationNumber" : "organisationRegistrationNumber";
    } else if (field === "tax_info.vat_number") {
        return isSole ? "soleProprietorVatNumber" : "organisationVatNumber";
    } else {
        return field;
    }
}

const actions = {
    resetErrors({commit}) {
        commit('resetErrors');
    },
    setErrors({commit, rootGetters}, {key, errors}) {
        const field = mapApiPathToField(key, rootGetters);
        commit('setErrors', {key: field, errors});
    },
    validate({commit, state, rootGetters}) {
        commit('resetErrors')
        return validateAndGenerateError(commit, state.api, rootGetters, rootGetters["essentials/getCountry"], state.data)
    },
    validateField({commit, state, rootGetters}, key) {
        validateAndSetValid(commit, businessRegistrationState.api, state.data, rootGetters["essentials/getCountry"], key)
    },
    validateAllFields({state, dispatch}) {
        Object.keys(state.data).forEach(key => dispatch('validateField', key))
    },
    resetDirty({commit}) {
        commit('resetDirty')
    },
    updateIncorporationDate({commit, dispatch}, value) {
        commit('updateIncorporationDate', msToyyyyMMdd(value));
        commit('setDirty')
        dispatch('validateField', 'incorporationDate')
    },
    updateCountrySpecificField({commit, dispatch}, {field, event}) {
        commit('updateCountrySpecificField', {field, value: event?.target?.value});
        commit('setDirty')
        dispatch('validateField', field)
    },
    updateField({commit, dispatch}, {key, event}: {key: BusinessRegistrationField, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromEvent({commit, dispatch}, {key, event}: {key: BusinessRegistrationField, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromValue({commit, dispatch}, {key, value}: {key: BusinessRegistrationField, value: any}) {
        commit('updateField', {key, value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    copyFromFieldErrorsResponse({commit}, subErrors) {
        commit('copyFromFieldErrorsResponse', subErrors)
    },
    copyFromMerchantApiResponse({commit}, merchantApiResponse) {
        commit('copyFromMerchantApiResponse', merchantApiResponse)
    },
};

const mutations = {
    resetErrors(state): void {
        state.errors = {}
    },
    setErrors(state, {key, errors}): void {
        state.errors[key] = errors;
    },
    setInvalid(state, key) {
        state.data[key].valid = false;
    },
    setValid(state, key) {
        state.data[key].valid = true;
    },
    setDirty(state) {
        state.dirty = true;
    },
    resetDirty(state) {
        state.dirty = false;
    },
    updateField(state, {key, value}) {
        state.data[key].value = value;
        delete state.errors[key];
    },
    updateIncorporationDate(state, incorporationDate: string): void {
        state.data.incorporationDate.value = incorporationDate;
        delete state.errors.incorporationDate;
    },
    updateCountrySpecificField(state, {field, value}): void {
        state.data[field].value = value;
        delete state.errors[field];
    },
    copyFromFieldErrorsResponse: function (state, subErrors): void {
        state.errors = copyFromErrorResponse(subErrors, state.data)
    },
    copyFromMerchantApiResponse(state, merchantApiResponse): void {
        state.data = copyFromApiResponse(merchantApiResponse, state.data);
        // Format phone number if it exists
    },
};

export const businessRegistration = {
    namespaced: true,
    state: businessRegistrationState,
    getters,
    actions,
    mutations,
};
